import { ExtractKeysExtend } from '../components/helpers/helpers';
import {
    AircraftThresholdType,
    AirportThresholdType,
    MonitorAircraftOptions,
    MonitorAircraftThreshold,
    MonitorAircraftThresholdAlerts,
    MonitorAirportOptions,
    MonitorAirportThreshold,
    MonitorAirportThresholdAlerts,
    MonitorAirportThresholdSettingsItem,
    MonitorThresholdAlert,
    ThresholdEnableState,
    ThresholdLevelActionType
} from '../types/thresholds';

export const commonAirportThresholdsDisplayOrder: AirportThresholdType[] = [
    AirportThresholdType.metarFlightCat,
    AirportThresholdType.metarVisibility,
    AirportThresholdType.metarCeiling,
    AirportThresholdType.metarWindSpeed,
    AirportThresholdType.metarWindGusts,
    AirportThresholdType.metarLastUpdate,
    AirportThresholdType.tafFlightCat,
    AirportThresholdType.tafVisibility,
    AirportThresholdType.tafCeiling,
    AirportThresholdType.tafWindSpeed,
    AirportThresholdType.tafLastUpdate,
    AirportThresholdType.departDelay,
    AirportThresholdType.arrivalDelay,
    AirportThresholdType.groundStop,
    AirportThresholdType.groundDelay,
    AirportThresholdType.faaClosure,
    AirportThresholdType.rvr,
    AirportThresholdType.diversionsTo,
    AirportThresholdType.diversionsFrom,
    AirportThresholdType.departureRate,
    AirportThresholdType.arrivalRate,
    AirportThresholdType.deIcingActive,
    AirportThresholdType.holdingInto,
    AirportThresholdType.monitorStatusChanged
] as const;

export const commonAircraftThresholdsDisplayOrder: AircraftThresholdType[] = [
    AircraftThresholdType.aptMonStatOrg,
    AircraftThresholdType.aptMonStatDst,
    AircraftThresholdType.aptMonStatAlt,
    AircraftThresholdType.minPreDptNoFPFiled,
    AircraftThresholdType.minPastPlanDepTime,
    AircraftThresholdType.minPastEstArrTime,
    AircraftThresholdType.taxiTime,
    AircraftThresholdType.milesOffRoute,
    AircraftThresholdType.milesFromDest,
    AircraftThresholdType.timeSinceLastPosUpd,
    AircraftThresholdType.diverting,
    AircraftThresholdType.holdingDetected,
    AircraftThresholdType.enterDrawingObject,
    AircraftThresholdType.inDrawingObject,
    AircraftThresholdType.exitDrawingObject,
    AircraftThresholdType.routeIntersectArea,
    AircraftThresholdType.routeNotIntersectArea,
    AircraftThresholdType.emergencyBeaconCodeRec,
    AircraftThresholdType.edctIssued,
    AircraftThresholdType.altitudeChange,
    AircraftThresholdType.volcAsh,
    AircraftThresholdType.sigmet,
    AircraftThresholdType.cnvSigmet,
    AircraftThresholdType.turbulence,
    AircraftThresholdType.edrTurbulence,
    AircraftThresholdType.icing,
    AircraftThresholdType.fobDiffActEst,
    AircraftThresholdType.altDiffActEst,
    AircraftThresholdType.goAround,
    AircraftThresholdType.iataTurbulence,
    AircraftThresholdType.pirepIce,
    AircraftThresholdType.pirepTurb,
    AircraftThresholdType.gtgnTurbulence,
    AircraftThresholdType.dsua,
    AircraftThresholdType.impactContrails,
    AircraftThresholdType.impactTcf,
    AircraftThresholdType.departureTimeChange,
    AircraftThresholdType.etaChange,
    AircraftThresholdType.positionUpdated,
    AircraftThresholdType.fobChanged,
    AircraftThresholdType.scheduleReceived,
    AircraftThresholdType.flightPlanReceived,
    AircraftThresholdType.flightPlanChanged,
    AircraftThresholdType.routeEteChanged,
    AircraftThresholdType.activeStatus,
    AircraftThresholdType.departedStatus,
    AircraftThresholdType.arrivedStatus,
    AircraftThresholdType.outReceived,
    AircraftThresholdType.offReceived,
    AircraftThresholdType.onReceived,
    AircraftThresholdType.inReceived,
    AircraftThresholdType.fpmLateAtPosition,
    AircraftThresholdType.fpmFobAlertLVL1,
    AircraftThresholdType.fpmFobAlertLVL2,
    AircraftThresholdType.fpmFobAlertLVL3,
    AircraftThresholdType.fpmLowAtPosition,
    AircraftThresholdType.fpmMinOffRouteAtPosition,
    AircraftThresholdType.fpmNMOffRouteAtPosition,
    AircraftThresholdType.fpmPosRepNotReceived,
    AircraftThresholdType.fpmMinPriorToEtopsEntry,
    AircraftThresholdType.fpmMinPriorToRdspchPoint,
    AircraftThresholdType.fpmTurbulenceAlert,
    AircraftThresholdType.fpmNewNotamForAirport,
    AircraftThresholdType.fpmNewNotamForFir,
    AircraftThresholdType.monitorStatusChanged
];

export const defaultAircraftThresholds: MonitorAircraftThreshold[] = [
    {
        type: AircraftThresholdType.aptMonStatOrg,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 0,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 2,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 3,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: {
            repeat: null,
            triggerOnImproving: null,
            value: 4,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        alerts: newAircraftAlerts(),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.aptMonStatDst,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 0,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 2,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 3,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: {
            repeat: null,
            triggerOnImproving: null,
            value: 4,
            unitIndex: null,
            actionType: ThresholdLevelActionType.withinMinutes,
            actionTimeMinutes: 30
        },
        alerts: newAircraftAlerts(),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.aptMonStatAlt,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 0,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 2,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 3,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: {
            repeat: null,
            triggerOnImproving: null,
            value: 4,
            unitIndex: null,
            actionType: ThresholdLevelActionType.withinMinutes,
            actionTimeMinutes: 30
        },
        alerts: newAircraftAlerts(),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.minPreDptNoFPFiled,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 120,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 60,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 30,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        alerts: newAircraftAlerts(),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.minPastPlanDepTime,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 10,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 20,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 30,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        alerts: newAircraftAlerts(),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.minPastEstArrTime,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 20,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 30,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 60,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        alerts: newAircraftAlerts(),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.taxiTime,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 30,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 60,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 100,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 150,
            unitIndex: null,
            actionType: ThresholdLevelActionType.withinMinutes,
            actionTimeMinutes: 10
        },
        emergency: null,
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.milesOffRoute,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 50,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 100,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        alerts: newAircraftAlerts(),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.timeSinceLastPosUpd,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 16,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 30,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.diverting,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        critical: null,
        emergency: null,
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.holdingDetected,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        warning: null,
        critical: null,
        emergency: null,
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.emergencyBeaconCodeRec,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: null,
        critical: null,
        emergency: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.immediate,
            actionTimeMinutes: null
        },
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.edctIssued,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        warning: null,
        critical: null,
        emergency: null,
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.volcAsh,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: null,
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.immediate,
            actionTimeMinutes: null
        },
        emergency: null,
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.sigmet,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: null,
        critical: null,
        emergency: null,
        alerts: newAircraftAlerts(),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.cnvSigmet,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        critical: null,
        emergency: null,
        alerts: newAircraftAlerts(),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.turbulence,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 2,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 3,
            unitIndex: null,
            actionType: ThresholdLevelActionType.withinMinutes,
            actionTimeMinutes: 15
        },
        emergency: null,
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.edrTurbulence,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 3,
            unitIndex: null,
            actionType: ThresholdLevelActionType.withinMinutes,
            actionTimeMinutes: 15
        },
        emergency: {
            repeat: null,
            triggerOnImproving: null,
            value: 5,
            unitIndex: null,
            actionType: ThresholdLevelActionType.immediate,
            actionTimeMinutes: null
        },
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.icing,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 2,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 3,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        alerts: newAircraftAlerts(),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.goAround,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: null,
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.iataTurbulence,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 3,
            unitIndex: null,
            actionType: ThresholdLevelActionType.withinMinutes,
            actionTimeMinutes: 15
        },
        emergency: {
            repeat: null,
            triggerOnImproving: null,
            value: 5,
            unitIndex: null,
            actionType: ThresholdLevelActionType.immediate,
            actionTimeMinutes: null
        },
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.pirepIce,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 0,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 2,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        alerts: newAircraftAlerts(),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.pirepTurb,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 2,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 3,
            unitIndex: null,
            actionType: ThresholdLevelActionType.withinMinutes,
            actionTimeMinutes: 15
        },
        emergency: {
            repeat: null,
            triggerOnImproving: null,
            value: 4,
            unitIndex: null,
            actionType: ThresholdLevelActionType.immediate,
            actionTimeMinutes: null
        },
        alerts: newAircraftAlerts(),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.gtgnTurbulence,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 3,
            unitIndex: null,
            actionType: ThresholdLevelActionType.withinMinutes,
            actionTimeMinutes: 15
        },
        emergency: {
            repeat: null,
            triggerOnImproving: null,
            value: 5,
            unitIndex: null,
            actionType: ThresholdLevelActionType.immediate,
            actionTimeMinutes: null
        },
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.dsua,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: null,
        warning: null,
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.withinMinutes,
            actionTimeMinutes: 15
        },
        emergency: null,
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.departureTimeChange,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 15,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 30,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 60,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        alerts: newAircraftAlerts(),
        ignoredInheritedLevels: []
    },
    {
        type: AircraftThresholdType.etaChange,
        priority: 50,
        enableState: ThresholdEnableState.enabled,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 15,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 30,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 60,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        alerts: newAircraftAlerts('eventList'),
        ignoredInheritedLevels: []
    }
];

export const defaultAirportThresholds: MonitorAirportThreshold[] = [
    {
        type: AirportThresholdType.metarFlightCat,
        enableState: 1,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 2,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 3,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 4,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.metarVisibility,
        enableState: 1,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 300,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 200,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 100,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 50,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.metarCeiling,
        enableState: 1,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 800,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 600,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 400,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 200,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.metarWindSpeed,
        enableState: 1,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 10,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 15,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 20,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 25,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.metarWindGusts,
        enableState: 1,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 15,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 20,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 25,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 30,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.metarLastUpdate,
        enableState: 1,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 65,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 70,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 80,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.tafFlightCat,
        enableState: 1,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 2,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 3,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 4,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.tafVisibility,
        enableState: 1,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 300,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 200,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 100,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 50,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.tafCeiling,
        enableState: 1,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 800,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 600,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 400,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 200,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.tafWindSpeed,
        enableState: 1,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 10,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 15,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 20,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 25,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.tafLastUpdate,
        enableState: 1,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 370,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 380,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 400,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.departDelay,
        enableState: 1,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 15,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 30,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 45,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 60,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.arrivalDelay,
        enableState: 1,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 15,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 30,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 45,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 60,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.groundStop,
        enableState: 1,
        info: null,
        watch: null,
        warning: null,
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.groundDelay,
        enableState: 1,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 15,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 30,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.faaClosure,
        enableState: 1,
        info: null,
        watch: null,
        warning: null,
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.rvr,
        enableState: 1,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 5000,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 4000,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 2400,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 1800,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.diversionsTo,
        enableState: 1,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 4,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 7,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 10,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.deIcingActive,
        enableState: 1,
        info: {
            repeat: null,
            triggerOnImproving: null,
            value: 1,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        watch: null,
        warning: null,
        critical: null,
        emergency: null,
        ignoredInheritedLevels: []
    },
    {
        type: AirportThresholdType.holdingInto,
        enableState: 1,
        info: null,
        watch: {
            repeat: null,
            triggerOnImproving: null,
            value: 2,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        warning: {
            repeat: null,
            triggerOnImproving: null,
            value: 5,
            unitIndex: null,
            actionType: ThresholdLevelActionType.none,
            actionTimeMinutes: null
        },
        critical: {
            repeat: null,
            triggerOnImproving: null,
            value: 10,
            unitIndex: null,
            actionType: ThresholdLevelActionType.possible,
            actionTimeMinutes: null
        },
        emergency: null,
        ignoredInheritedLevels: []
    }
];

export const defaultAirportOptions: MonitorAirportThresholdSettingsItem[] = [
    {
        type: AirportThresholdType.metarFlightCat,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.metarVisibility,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.metarCeiling,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.metarWindSpeed,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.metarWindGusts,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.tafFlightCat,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.tafVisibility,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.tafCeiling,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.tafWindSpeed,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.departDelay,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.arrivalDelay,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.groundStop,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.groundDelay,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.faaClosure,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.rvr,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.diversionsTo,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.diversionsFrom,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.departureRate,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.arrivalRate,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.deIcingActive,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    },
    {
        type: AirportThresholdType.holdingInto,
        priority: 50,
        alerts: newAirportAlerts('eventList')
    }
];

export const defaultAircraftMonitorOptions = {
    monitorOriginDepartureMinMinutes: 240,
    monitorOriginWithoutDepartureTime: true,
    monitorDestinationDepartureMinMinutes: 360,
    monitorDestinationWithoutArrivalTime: true,
    monitorAltDepartureMinMinutes: 360,
    monitorAltWithoutArrivalTime: true,
    emergencyBeacons: [
        { code: 7500, text: 'Hijacking' },
        { code: 7600, text: 'Comm failure' },
        { code: 7700, text: 'Emergency' },
        { code: 7777, text: 'Military intercept' }
    ],
    ignoredCompanyBeaconCodes: [],
    altitudeChangeWindowMinutes: 5,
    suppressAltitudeMonitorMiles: 50,
    monitorImpactDistance: {
        volcanicAsh: 0,
        sigmet: 0,
        convSigment: 0,
        dsua: 0,
        edrTurbulence: 0,
        turbulence: 0,
        icing: 0,
        contrail: 0,
        tcf: 0
    }
} satisfies MonitorAircraftOptions;

export const defaultAirportMonitorOptions = {
    tafWindowHours: 24,
    airportMonitorStatusInclude: {
        originThresholds: true,
        destinationThresholds: true,
        alternateThresholds: true
    }
} satisfies MonitorAirportOptions;

export function newAircraftAlerts(
    ...params: Extract<ExtractKeysExtend<MonitorAircraftThresholdAlerts, MonitorThresholdAlert>, string>[]
): MonitorAircraftThresholdAlerts {
    const alerts: MonitorAircraftThresholdAlerts = {
        alertOnScheduled: { value: true, minLevel: null },
        alertOnPlanned: { value: true, minLevel: null },
        alertOnActive: { value: true, minLevel: null },
        eventList: { value: null, minLevel: null },
        eventListAck: { value: null, minLevel: null },
        eventListImportant: { value: null, minLevel: null },
        email: { value: null, minLevel: null },
        logFile: { value: null, minLevel: null },
        popup: { value: null, minLevel: null },
        osAlert: { value: null, minLevel: null },
        aircraftTag: { value: null, minLevel: null },
        zoomToAircraft: { value: null, minLevel: null },
        overrideColor: { value: null, minLevel: null },
        enableMarker: { value: null, minLevel: null },
        voice: { value: null, minLevel: null },
        playSound: { value: null, minLevel: null },
        overrideColorHex: null
    };

    params.forEach((x) => (alerts[x].value = true));
    return alerts;
}

export function newAirportAlerts(
    ...params: Extract<ExtractKeysExtend<MonitorAirportThresholdAlerts, MonitorThresholdAlert>, string>[]
): MonitorAirportThresholdAlerts {
    const alerts: MonitorAirportThresholdAlerts = {
        eventList: { value: null, minLevel: null },
        eventListAck: { value: null, minLevel: null },
        eventListImportant: { value: null, minLevel: null },
        email: { value: null, minLevel: null },
        logFile: { value: null, minLevel: null },
        popup: { value: null, minLevel: null },
        osAlert: { value: null, minLevel: null },
        overrideColor: { value: null, minLevel: null },
        voice: { value: null, minLevel: null },
        playSound: { value: null, minLevel: null },
        overrideColorHex: null
    };

    params.forEach((x) => (alerts[x].value = true));
    return alerts;
}
